import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useAuth } from '../../auth/UseAuth'
import { ImgHelper } from '../../helpers/ImgHelper'
import { useSettings } from '../../hooks/useSettings'
import { useLazyFindContractsQuery } from '../../redux/api/contractsApi'
import { emptyGuid } from '../../types/constants'
import { UserMenuComponent } from '../common/UserMenu/UserMenuComponent'
import { ContractAnonymousViewModel } from '@ail/contracts-api'
import { LoadingSpinner } from '../common/LoadingSpinner/LoadingSpinner'

export function HomeComponent() {
  const [chassisNumber, setChassisNumber] = useState('')
  const [licenseNumber, setLicenseNumber] = useState('')
  const [searchMode, setSearchMode] = useState<'licenseNumber' | 'chassisNumber'>()
  const { user, manager } = useAuth()
  const [findContracts, contracts] = useLazyFindContractsQuery()
  const tooManyRequests =
    !contracts.isFetching && contracts.error && 'isApiException' in contracts.error && contracts.error.status === 429
  const noResults = !contracts.isFetching && !contracts.error && !contracts.data?.length
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [contractHistory, updateContractHistory] = useSettings('iLinkContractHistory', { remote: false })
  const contractHistoryKey = user?.id ?? emptyGuid
  const currentContractHistory = contractHistory[contractHistoryKey] ?? []

  const searchByChassisNumber = async () => {
    if (chassisNumber.length >= 7) {
      try {
        const results = await findContracts({ chassisNumber }, true).unwrap()
        processResults(results)
      } finally {
        setSearchMode('chassisNumber')
      }
    }
  }

  const searchByLicenseNumber = async () => {
    if (user && licenseNumber.length >= 3) {
      try {
        const results = await findContracts({ licenseNumber }, true).unwrap()
        processResults(results)
      } finally {
        setSearchMode('licenseNumber')
      }
    }
  }

  const processResults = (contracts: ContractAnonymousViewModel[]) => {
    if (contracts.length) {
      const historyLength = user?.isInternalUser ? 10 : 3
      const foundContract = contracts[0]
      if (!currentContractHistory.some((c) => c.id === foundContract.id)) {
        const newContractHistory = [...currentContractHistory, foundContract].slice(-historyLength)
        updateContractHistory({
          ...contractHistory,
          [contractHistoryKey]: newContractHistory,
        })
      }
      navigate(`/results/${foundContract.id}`)
    }
  }

  useEffect(() => {
    if (searchMode === 'licenseNumber' && !licenseNumber.length) {
      setSearchMode(undefined)
    } else if (searchMode === 'chassisNumber' && !chassisNumber.length) {
      setSearchMode(undefined)
    }
  }, [chassisNumber, licenseNumber, searchMode])

  return (
    <div className="container">
      <div className="home">
        <img
          alt="InterfleetLink"
          className={user ? 'logo hidden' : 'logo'}
          src="/img/interfleetlink_logo.svg"
          width="390"
          height="87"
          data-fallback="/img/interfleetlink_logo.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
        <img
          alt="InterfleetLink"
          className="logo hidden"
          src="/img/ilink-amag.svg"
          width="482"
          height="80"
          data-fallback="/img/ilink-amag.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />
        <img
          alt="InterfleetLink"
          className="logo hidden"
          src="/img/ilink-galliker.svg"
          width="474"
          height="80"
          data-fallback="/img/ilink-galliker.png"
          onError={(e) => ImgHelper.handleImageLoadingFailure(e)}
        />

        {!user && !currentContractHistory?.length ? (
          <div className="header">
            <div className="toolArea">
              <UserMenuComponent />
            </div>
          </div>
        ) : null}

        <div className="row">
          <div className="col-12">
            <p>{t('WELCOME_TITLE')}</p>
            <span className="homeText">
              <Trans>{t('WELCOME_TEXT')}</Trans>
            </span>
          </div>
          <div className="col-12 col-md-6">
            <div className="box">
              <div className="boxTitle">
                <span>{t('VIN_NUMBER')}</span>
                <div style={chassisNumber.length > 0 ? { right: '60px' } : undefined} className="searchLeft">
                  <i className="icon-info"></i>
                </div>
              </div>
              <div className={`inputArea ${searchMode === 'chassisNumber' && noResults ? 'searchFailed' : ''}`}>
                <input
                  value={chassisNumber}
                  onKeyDown={(e) => e.key === 'Enter' && searchByChassisNumber()}
                  onChange={(e) => setChassisNumber(e.target.value)}
                  type="text"
                  placeholder={t('VIN_NUMBER_SEARCH') ?? ''}
                />
                <div className="rightBox">
                  {searchMode === 'chassisNumber' && noResults && (
                    <div className="noResults">{t('NO_RESULTS_FOUND')}</div>
                  )}
                  {searchMode === 'chassisNumber' && tooManyRequests && (
                    <div className="noResults">{t('TOO_MANY_REQUESTS')}</div>
                  )}
                  {searchMode === 'chassisNumber' && contracts.isFetching && (
                    <div className="customLoaderParent">
                      <LoadingSpinner />
                    </div>
                  )}
                  {chassisNumber.length > 0 && <i className="icon-clear" onClick={() => setChassisNumber('')}></i>}
                  <i className="icon-search" onClick={() => searchByChassisNumber()}></i>
                </div>
              </div>
            </div>
          </div>
          <div style={{ position: 'relative' }} className="col-12 col-md-6">
            <div className="box">
              <div className="boxTitle">{t('PLATE_NUMBER')}</div>
              <div
                className={`inputArea ${searchMode === 'licenseNumber' && noResults ? 'searchFailed' : ''}${!user ? 'disabled' : ''}`}
              >
                <div className="plate">
                  <img alt="CH" className="flag" src="/img/kantons/CH.svg" width="15" height="17" />
                  <input
                    value={licenseNumber}
                    disabled={!user}
                    onChange={(e) => setLicenseNumber(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchByLicenseNumber()}
                    type="text"
                    className="plateNumber"
                    placeholder="XX123456"
                  />
                </div>
                <div className="rightBox">
                  {searchMode === 'licenseNumber' && noResults && (
                    <div className="noResults">{t('NO_RESULTS_FOUND')}</div>
                  )}
                  {searchMode === 'licenseNumber' && contracts.isFetching && (
                    <div className="customLoaderParent">
                      <LoadingSpinner />
                    </div>
                  )}
                  {licenseNumber.length > 0 && <i className="icon-clear" onClick={() => setLicenseNumber('')}></i>}
                  {!!user && <i className="icon-search" onClick={() => searchByLicenseNumber()}></i>}
                </div>
              </div>
            </div>
            {!user && (
              <div className="popLogged bounce-in">
                <div className="triangle"></div>
                <div className="popLoggedArea">
                  <Trans components={{ span: <span className="overlayText"></span> }}>{t('REGISTER_POPUP')}</Trans>
                </div>
              </div>
            )}
          </div>
        </div>
        {!user && (
          <div className="row logText">
            <div className="col-12">
              <span className="homeText">
                <Trans>{t('WELCOME_TEXT2')}</Trans>
              </span>
            </div>
            <div className="col-12">
              <div className="box">
                <div className="flex between">
                  <img src="/img/consulente3.png" className="helpImg" />
                  <div>
                    <h3>
                      <strong>{t('LOGIN_TITLE')}</strong>
                      {t('LOGIN_TEXT')}
                    </h3>
                    <div className="btnArea">
                      <button className="btn" onClick={() => manager.signinRedirect()}>
                        {t('SIGN_IN')}
                      </button>
                      {/* <NavLink to="/registration" title="Register" className="btn">
                          {t('REGISTER')}
                        </NavLink> */}
                    </div>
                  </div>
                  <img src="/img/consulente2.png" className="helpImg" />
                </div>
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/anfragen-zugriffe.png" />
                </div>
                {t('CONDITION_1')}
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/reparatur.png" />
                </div>
                {t('CONDITION_2')}
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/upload.png" />
                </div>
                {t('CONDITION_3')}
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/online-meldung.png" />
                </div>
                {t('CONDITION_4')}
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/status-aktualisierungen.png" />
                </div>
                {t('CONDITION_5')}
              </div>
            </div>
            <div className="col-12 col-md-2">
              <div className="box">
                <div className="logImg">
                  <img src="/img/abrechnung.png" />
                </div>
                {t('CONDITION_6')}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
